import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Badge,
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  SimpleGrid,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import Navbar from './Navbar';
import AuthContext from '../context/AuthContext';
import { API_BASE_URL } from '../config/adminConfig';

function OnboardingFunnel() {
  const { token } = useContext(AuthContext);
  const [funnelData, setFunnelData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFunnel = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/admin/onboardingFunnel`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setFunnelData(response.data);
      } finally {
        setLoading(false);
      }
    };

    fetchFunnel();
  }, [token]);

  const sectionRows = useMemo(() => funnelData?.sectionFunnel || [], [funnelData]);
  const dropOffRows = useMemo(() => funnelData?.dropOffs || [], [funnelData]);
  const summary = funnelData?.summary || {};
  const isFallback = Boolean(funnelData?.meta?.isFallback);
  const cardBg = useColorModeValue('white', 'gray.800');
  const pageBg = useColorModeValue('gray.50', 'gray.900');

  if (loading) {
    return (
      <Box minH="100vh" bg={pageBg}>
        <Navbar />
        <Box display="flex" justifyContent="center" py={20}>
          <Spinner size="xl" />
        </Box>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={pageBg}>
      <Navbar />
      <Box maxW="7xl" mx="auto" p={6}>
        <Heading size="xl" mb={2}>
          Onboarding Funnel
        </Heading>
        <Text color="gray.600" mb={8}>
          Track completion, abandonment and the exact onboarding step where users stop.
        </Text>

        {isFallback ? (
          <Card bg={cardBg} shadow="sm" mb={6}>
            <CardBody>
              <Badge colorScheme="orange" mb={3}>
                Derived historical view
              </Badge>
              <Text color="gray.600">
                Live onboarding event tracking has not accumulated session history yet.
                This view is temporarily derived from saved onboarding answers so the
                admin portal can still show legacy completion data.
              </Text>
            </CardBody>
          </Card>
        ) : null}

        <SimpleGrid columns={[1, 2, 3, 6]} spacing={4} mb={8}>
          <MetricCard label="Sessions" value={summary.totalSessions} />
          <MetricCard label="Completed" value={summary.completedSessions} tone="blue" />
          <MetricCard label="Active" value={summary.activeSessions} tone="green" />
          <MetricCard label="Abandoned" value={summary.abandonedSessions} tone="red" />
          <MetricCard
            label="Completion Rate"
            value={`${summary.completionRate || 0}%`}
            tone="purple"
          />
          <MetricCard
            label="Avg Time"
            value={`${summary.averageCompletionSeconds || 0}s`}
            tone="orange"
          />
        </SimpleGrid>

        <SimpleGrid columns={[1, 1, 2]} spacing={6}>
          <Card bg={cardBg} shadow="sm">
            <CardHeader>
              <Heading size="md">Section Funnel</Heading>
            </CardHeader>
            <CardBody>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Section</Th>
                    <Th isNumeric>Entered</Th>
                    <Th isNumeric>Completed</Th>
                    <Th isNumeric>Drop-off</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {sectionRows.map(section => {
                    const dropOff = Math.max((section.entered || 0) - (section.completed || 0), 0);
                    return (
                      <Tr key={section.sectionKey}>
                        <Td textTransform="capitalize">{section.sectionKey}</Td>
                        <Td isNumeric>{section.entered}</Td>
                        <Td isNumeric>{section.completed}</Td>
                        <Td isNumeric>{dropOff}</Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </CardBody>
          </Card>

          <Card bg={cardBg} shadow="sm">
            <CardHeader>
              <Heading size="md">Top Drop-off Points</Heading>
            </CardHeader>
            <CardBody>
              {dropOffRows.length === 0 ? (
                <Text color="gray.600">No abandoned onboarding sessions yet.</Text>
              ) : (
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Question</Th>
                      <Th>Section</Th>
                      <Th isNumeric>Count</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {dropOffRows.slice(0, 10).map(dropOff => (
                      <Tr key={dropOff.questionId}>
                        <Td maxW="360px">
                          <Text fontWeight="600">{dropOff.questionId}</Text>
                          <Text color="gray.600" noOfLines={2}>
                            {dropOff.questionText}
                          </Text>
                        </Td>
                        <Td>
                          <Badge colorScheme="blue" textTransform="capitalize">
                            {dropOff.sectionKey}
                          </Badge>
                        </Td>
                        <Td isNumeric>{dropOff.count}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </SimpleGrid>

        <Text mt={6} color="gray.500" fontSize="sm">
          Updated at: {funnelData?.updatedAt ? new Date(funnelData.updatedAt).toLocaleString() : 'n/a'}
        </Text>
      </Box>
    </Box>
  );
}

function MetricCard({ label, value, tone = 'gray' }) {
  const colorMap = {
    gray: 'gray.900',
    blue: 'blue.500',
    green: 'green.500',
    red: 'red.500',
    purple: 'purple.500',
    orange: 'orange.500',
  };

  return (
    <Card shadow="sm">
      <CardBody>
        <Stat>
          <StatLabel>{label}</StatLabel>
          <StatNumber color={colorMap[tone] || colorMap.gray}>{value ?? 0}</StatNumber>
          <StatHelpText mb={0}>Realtime admin metric</StatHelpText>
        </Stat>
      </CardBody>
    </Card>
  );
}

export default OnboardingFunnel;
